import React, { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import LandingHeader from '../components/landing-page/LandingHeader';
import Footer from '../components/landing-page/Footer';
import { footerNavigation } from '../components/landing-page/contentSections';
import { DemoFormFeedback } from '../components/DemoFormFeedback';
import { usePublicFormSubmit } from '../hooks/usePublicFormSubmit';
import { getCareerJob } from './careersData';

export default function CareerJobPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const job = jobId ? getCareerJob(jobId) : undefined;
  const { status, runSubmit, isSubmitting } = usePublicFormSubmit();

  const [form, setForm] = useState({
    name: '',
    email: '',
    linkedin: '',
    note: '',
  });

  if (!job) {
    return <Navigate to='/careers' replace />;
  }

  const onChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const ok = await runSubmit('/public/careers-application', {
      jobId: job.id,
      jobTitle: job.title,
      name: form.name,
      email: form.email,
      linkedin: form.linkedin,
      note: form.note,
    });
    if (ok) setForm({ name: '', email: '', linkedin: '', note: '' });
  };

  return (
    <div className='min-h-screen bg-white dark:bg-boxdark-2 dark:text-white'>
      <LandingHeader />
      <main className='mx-auto max-w-3xl px-6 pb-24 pt-28 lg:px-8'>
        <Link
          to='/careers'
          className='inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-yellow-600 dark:text-gray-400 dark:hover:text-yellow-500'
        >
          ← All openings
        </Link>

        <header className='mt-8 border-b border-gray-200 pb-10 dark:border-strokedark'>
          <div className='flex flex-wrap gap-2'>
            <span className='rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-900 dark:bg-gray-200 dark:text-gray-900'>
              {job.team}
            </span>
            <span className='rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-900 dark:bg-gray-200 dark:text-gray-900'>
              {job.location}
            </span>
            <span className='rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-900 dark:bg-gray-200 dark:text-gray-900'>
              {job.employmentType}
            </span>
          </div>
          <h1 className='mt-6 text-3xl font-bold tracking-tight text-gray-900 dark:text-white md:text-4xl'>
            {job.title}
          </h1>
          <p className='mt-3 text-lg text-gray-600 dark:text-gray-300'>{job.summary}</p>

          {job.externalApplyUrl && (
            <a
              href={job.externalApplyUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='mt-8 inline-flex rounded-lg bg-yellow-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-yellow-600'
            >
              Apply for this role
            </a>
          )}
        </header>

        <div className='mt-10 space-y-10'>
          {job.sections.map((section) => (
            <section key={section.heading}>
              <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>{section.heading}</h2>
              <div className='mt-4 space-y-4 text-gray-700 dark:text-gray-300'>
                {section.paragraphs.map((p, i) => (
                  <p key={i} className='leading-relaxed'>
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {!job.externalApplyUrl ? (
          <div className='mt-14 rounded-xl border border-gray-200 bg-white p-6 text-gray-900 shadow-sm ring-1 ring-gray-900/5 dark:border-gray-200 dark:bg-white dark:text-gray-900'>
            <h2 className='text-xl font-semibold text-gray-900'>Apply</h2>
            <p className='mt-2 text-sm text-gray-600'>
              Share a few details. We read every application and respond within several business days.
            </p>
            <form onSubmit={onSubmit} className='mt-6 space-y-4'>
              <div>
                <label htmlFor='career-name' className='mb-2 block text-sm font-medium text-gray-700'>
                  Full name
                </label>
                <input
                  id='career-name'
                  required
                  autoComplete='name'
                  value={form.name}
                  onChange={(e) => onChange('name', e.target.value)}
                  className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500 dark:border-gray-300 dark:bg-white dark:text-gray-900'
                />
              </div>
              <div>
                <label htmlFor='career-email' className='mb-2 block text-sm font-medium text-gray-700'>
                  Email
                </label>
                <input
                  id='career-email'
                  type='email'
                  required
                  autoComplete='email'
                  value={form.email}
                  onChange={(e) => onChange('email', e.target.value)}
                  className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500 dark:border-gray-300 dark:bg-white dark:text-gray-900'
                />
              </div>
              <div>
                <label htmlFor='career-linkedin' className='mb-2 block text-sm font-medium text-gray-700'>
                  LinkedIn or portfolio URL
                </label>
                <input
                  id='career-linkedin'
                  type='url'
                  value={form.linkedin}
                  onChange={(e) => onChange('linkedin', e.target.value)}
                  placeholder='https://'
                  className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500 dark:border-gray-300 dark:bg-white dark:text-gray-900'
                />
              </div>
              <div>
                <label htmlFor='career-note' className='mb-2 block text-sm font-medium text-gray-700'>
                  Why this role?
                </label>
                <textarea
                  id='career-note'
                  required
                  rows={5}
                  value={form.note}
                  onChange={(e) => onChange('note', e.target.value)}
                  className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500 dark:border-gray-300 dark:bg-white dark:text-gray-900'
                />
              </div>
              <button
                type='submit'
                disabled={isSubmitting}
                className='rounded-md bg-yellow-500 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-70'
              >
                {isSubmitting ? 'Submitting...' : 'Submit application'}
              </button>
              <DemoFormFeedback
                tone='light'
                status={status}
                successMessage='Thanks — we received your application.'
                errorMessage='Something went wrong. Please try again.'
              />
            </form>
          </div>
        ) : (
          <p className='mt-14 text-center text-sm text-gray-500 dark:text-gray-400'>
            Questions?{' '}
            <Link to='/support' className='font-medium text-yellow-600 hover:underline dark:text-yellow-500'>
              Contact us
            </Link>{' '}
            and reference <span className='font-medium text-gray-700 dark:text-gray-300'>{job.title}</span>.
          </p>
        )}
      </main>
      <Footer footerNavigation={footerNavigation} />
    </div>
  );
}
